import { useState, useEffect } from 'react';
import BackButton from '../BackButton';
import { Button } from '../../Button';
import Input from '../../Input';
import FormLabel from '../FormLabel';

interface InviteAccountStepProps {
  formData: {
    name: string;
    email: string;
    password: string;
    password_confirm: string;
  };
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  onBack: () => void;
  canProceed: boolean;
  invitationToken?: string; // 招待トークンを受け取る
}

export default function InviteAccountStep({
  formData,
  onInputChange,
  onSubmit,
  onBack,
  canProceed,
}: InviteAccountStepProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [errors, setErrors] = useState<{
    password?: string;
    password_confirm?: string;
  }>({});

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  // CSRFトークンを取得
  useEffect(() => {
    const metaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    if (metaTag) {
      setCsrfToken(metaTag.content);
    } else {
      const fetchCSRFToken = async () => {
        try {
          const response = await fetch('/api/csrf_token');
          if (response.ok) {
            const data = await response.json();
            setCsrfToken(data.authenticity_token);
          }
        } catch (error) {
          console.error('CSRF token取得エラー:', error);
        }
      };
      fetchCSRFToken();
    }
  }, []);

  const handleSubmit = () => {
    // パスワードが一致しているか確認
    if (formData.password !== formData.password_confirm) {
      setErrors({
        password: 'パスワードが一致していません',
        password_confirm: 'パスワードが一致していません',
      });
      return;
    }

    onSubmit();
  };

  return (
    <>
      {/* Back Button */}
      <BackButton onBack={onBack} />

      {/* Title */}
      <div className="mb-12 text-center">
        <h2 className="my-0 text-2xl font-bold text-black">アカウントを作成</h2>
      </div>

      {/* Form */}
      <div className="w-full max-w-md space-y-6">
        {/* CSRF Token */}
        <input type="hidden" name="authenticity_token" value={csrfToken} />

        {/* Name Field */}
        <div>
          <FormLabel>お名前</FormLabel>
          <Input
            type="text"
            name="invitation[name]"
            className="w-full"
            value={formData.name}
            onChange={onInputChange}
            placeholder="例）山田太郎"
            required
          />
        </div>

        {/* Email Field */}
        <div>
          <FormLabel>メールアドレス</FormLabel>
          <Input
            type="email"
            name="invitation[email]"
            className="w-full cursor-not-allowed text-gray-500"
            value={formData.email}
            disabled
          />
        </div>

        {/* Password Field */}
        <div>
          <FormLabel>パスワード</FormLabel>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              name="invitation[password]"
              value={formData.password}
              onChange={onInputChange}
              className="w-full pr-10"
              required
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute top-1/2 right-3 -translate-y-1/2 transform bg-white"
            >
              {showPassword ? (
                <img
                  src="/agent/icon-eye-visible.svg"
                  alt="パスワードを非表示にする"
                  width="24"
                  height="24"
                />
              ) : (
                <img
                  src="/agent/icon-eye-hidden.svg"
                  alt="パスワードを表示する"
                  width="24"
                  height="24"
                />
              )}
            </button>
          </div>
          {errors.password && <p className="mt-2 text-sm text-red-600">{errors.password}</p>}
        </div>

        {/* Password Confirmation Field */}
        <div>
          <FormLabel>確認パスワード</FormLabel>
          <div className="relative">
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              name="invitation[password_confirm]"
              value={formData.password_confirm}
              onChange={onInputChange}
              className="w-full pr-10"
              required
            />
            <button
              type="button"
              onClick={toggleConfirmPasswordVisibility}
              className="absolute top-1/2 right-3 -translate-y-1/2 transform bg-white"
            >
              {showConfirmPassword ? (
                <img
                  src="/agent/icon-eye-visible.svg"
                  alt="確認パスワードを非表示にする"
                  width="24"
                  height="24"
                />
              ) : (
                <img
                  src="/agent/icon-eye-hidden.svg"
                  alt="確認パスワードを表示する"
                  width="24"
                  height="24"
                />
              )}
            </button>
          </div>
          {errors.password_confirm && (
            <p className="mt-2 text-sm text-red-600">{errors.password_confirm}</p>
          )}
        </div>

        {/* Submit Button */}
        <div className="mx-auto w-[320px] pt-6">
          <Button disabled={!canProceed} color="primary" size="lg" full onClick={handleSubmit}>
            エージェントハブに参加する
          </Button>
        </div>
      </div>
    </>
  );
}
